<script setup lang="ts">
import Button from '@/components/ui/button/Button.vue'
import { AlertSettingsViewModel } from './viewmodels/AlertSettings.viewmodel'
import { AlertSettingsRepositoryApiAdapter } from '@/@core/fire-risk-settings/infra/alert-settings-repository-adapter/AlertSettingsRepositoryApi.adapter'
import SidePanel from '@/components/ui/sidePanel/SidePanel.vue'
import AlertSettingsForm from './components/AlertSettingsForm.vue'
import { onBeforeMount, provide, watch } from 'vue'
import BaseTable from '../../../../ui/components/baseTable/BaseTable.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import Input from '@/components/ui/input/Input.vue'
import EmptyTable from '@/components/ui/emty-table/EmptyTable.vue'
import ContextMenu from '@/components/ui/contextMenu/ContextMenu.vue'
import ConfirmationModal from '@/components/ui/confirmation-modal/ConfirmationModal.vue'
import SimpleTag from '@/@web/ui/components/SimpleTag.vue'
import { fireRiskApi } from '@/services/clients/AxiosClients'
import { MonitoredAreaRepositoryApiAdapter } from '@/@core/monitored-area/infra/monitored-area-repository-adapter/MonitoredAreaRepositoryApi.adapter'
import { useMessages } from '@/composables/useMessages'
import i18n from '@/VueI18n'

const { addMessage } = useMessages()

const repository = new AlertSettingsRepositoryApiAdapter(fireRiskApi)

const monitoredAreaRepository = new MonitoredAreaRepositoryApiAdapter(fireRiskApi, (skipped) => {
	addMessage({
		title: i18n.t('someFarmsNotDisplayed') as string,
		description: i18n.t('farmsWithInvalidGeometryHidden', { count: skipped.length }) as string,
		type: 'info',
	})
})

const vm = new AlertSettingsViewModel(repository, monitoredAreaRepository)

provide('alert-settings-view-model', vm)

watch(vm.openForm, (value) => {
	if (!value) vm.resetAlertSettingsState()
})

onBeforeMount(() => vm.getAlertSettings())
</script>

<template>
	<main>
		<div class="flex justify-between my-4">
			<div class="flex gap-4">
				<Button
					icon="fa6-solid:trash"
					text="excluir"
					class="!outline-none"
					:disabled="!vm.registry.entriesToDelete.value.length"
					:class="[!vm.alertSettingsList.value?.length ? 'disabled:opacity-70' : '']"
					danger
					simple
					@click="vm.showDeletePopup.value = true"
				/>
				<Button
					text="cadastrar"
					@click="vm.openForm.value = true"
				/>
			</div>
			<div
				style="display: flex"
				class="gap-4"
			>
				<Input
					v-model="vm.registry.searchTerm.value"
					search
					placeholder="Pesquisar"
					@clear="vm.registry.searchTerm.value = ''"
				/>
				<Button
					:disabled="!vm.alertSettingsList.value?.length"
					text="exportar"
					secondary
					simple
					@click="() => vm.exportDataTable()"
				/>
			</div>
		</div>

		<base-table
			:checked.sync="vm.registry.entriesToDelete.value"
			:colunas="vm.colunas.value"
			:data="vm.registry.pageData.value"
			@sort="vm.registry.handleSort($event)"
		>
			<template #custom-edit="dado">
				<ContextMenu
					:item-id="dado.id"
					:options="vm.kebabOptions()"
					@editar="vm.loadAlertSettings(dado.id)"
				/>
			</template>

			<template #monitoredArea="alert">
				<div class="flex gap-1">
					<simple-tag
						v-for="area in alert.monitoredAreas.slice(0, 2)"
						:key="area.id"
						:label="area.name"
					/>
					<simple-tag
						v-if="alert.monitoredAreas.length > 2"
						:label="`+${alert.monitoredAreas.length - 2} ${alert.monitoredAreas.length - 2 === 1 ? 'área' : 'áreas'}`"
					/>
				</div>
			</template>
			<template #buffer="alert">
				<span>{{ alert.buffer }} km</span>
			</template>
			<template #minDetectionConfidence="alert">
				<span>{{ alert.minDetectionConfidence }}%</span>
			</template>
		</base-table>

		<pagination
			v-if="vm.registry.pageData.value?.length"
			:key="vm.registry.searchTerm.value"
			class="mt-4"
			:total="vm.alertSettingsList.value?.length || 0"
			:page="vm.registry.page.value"
			:page-size="vm.registry.pageSize.value"
			@paginationSettings="vm.registry.onPageChange"
		/>

		<empty-table
			v-if="!vm.registry.pageData.value.length"
			:key="vm.registry.searchTerm.value"
			registry-name="Configuração de alerta"
			:search="vm.registry.searchTerm.value"
			@clear="vm.registry.searchTerm.value = ''"
		/>
		<side-panel
			:change-is-visible="() => (vm.openForm.value = false)"
			:is-visible="vm.openForm.value"
			backdrop
		>
			<alert-settings-form
				:loading="vm.fetching.value"
				@cancel="() => (vm.openForm.value = false)"
				@submit="() => vm.createAlertSettings()"
			/>
		</side-panel>
		<confirmation-modal
			v-if="vm.showDeletePopup.value"
			title="Tem certeza que deseja excluir essa(s) configuração(ões) de alerta?"
			subtitle="A ação não poderá ser desfeita."
			:loading="vm.fetching.value"
			:items="vm.getAlertSettingsToDelete()"
			@delete="vm.deleteAlertSettings()"
			@cancel="vm.showDeletePopup.value = false"
		/>
	</main>
</template>

<style scoped>
:deep(.container__crudform__panel.container__crudform__panel__visible) {
	width: 610px;
}
</style>
